import { viewerNote as deskViewerNote, type Decision } from "../dashboard/deskLogic";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

export { longDay, orderDecisions, percent, shortDay, waitingTotal, type Decision } from "../dashboard/deskLogic";

/**
 * The head teacher's dashboard: decisions waiting for them and how the school
 * is running today. Mirrors apps/dashboard/headteacher.py.
 */

export interface ClassRegister {
  id: number; name: string; level: string; class_teacher: string; learners: number
  marked: number; present: number; late: number; absent: number; excused: number
}
export interface AttendanceDay { date: string; marked: number; in_school: number; rate: number | null }
export interface SchoolEvent { id: number; name: string; start_date: string; end_date: string; start_time: string | null; venue: string }
export interface HeadTeacherDesk {
  generated_at: string
  today: string
  viewer_role: string
  decisions: Decision[]
  attendance: { learners: number; marked: number; in_school: number; absent: number; rate: number | null; classes_marked: number; classes: ClassRegister[] }
  attendance_days: AttendanceDay[]
  school_today: {
    staff_on_leave: { name: string; leave_type: string; until: string }[] | null
    open_emergencies: number; serious_incidents: number; attendance_alerts: number
    sick_bay_visits: number; meetings_today: number; events: SchoolEvent[]
  }
}

type Tone = { label: string; dot: string };
/** Whether a class's day register has been taken today. */
export function registerStatus(row: Pick<ClassRegister, "marked" | "learners">): Tone {
  if (!row.marked) return { label: "Not taken", dot: "bg-amber-500" };
  if (row.marked < row.learners) return { label: "Part taken", dot: "bg-sky-500" };
  return { label: "Taken", dot: "bg-emerald-500" };
}
export const inSchool = (row: Pick<ClassRegister, "present" | "late">) => row.present + row.late;
export const classRate = (row: ClassRegister) => (row.marked ? Math.round((inSchool(row) / row.marked) * 1000) / 10 : null);

export const registerConfig: RegisterConfig<ClassRegister> = {
  search: (row) => `${row.name} ${row.level} ${row.class_teacher}`,
  views: [
    { key: "all", label: "All" },
    { key: "missing", label: "Not taken", match: (row) => !row.marked },
    { key: "taken", label: "Taken", match: (row) => row.marked > 0 },
    { key: "absent", label: "With absences", match: (row) => row.absent > 0 },
  ],
  sorts: [
    { key: "school", label: "School order", compare: () => 0 },
    { key: "absent", label: "Most absent", compare: reversed(byNumber((row) => row.absent)) },
    { key: "rate", label: "Lowest attendance", compare: byNumber((row) => classRate(row) ?? 101) },
    { key: "name", label: "Class name", compare: byText((row) => row.name) },
  ],
};

/** What the viewer is looking at, when they are not the head teacher themselves. */
export const viewerNote = (role: string) => deskViewerNote(role, "head_teacher", "the head teacher's dashboard");
